import { useMemo } from 'react';
import {
  Box, Typography, Stack, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Alert, CircularProgress, Link, Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';

// FEAT-020 — 지역 도달성 탭. "러시아에서 접속이 되는가" 에 화면으로 답한다.
//
// 🔴 '지역 차등' 과 '서비스 장애' 를 섞지 않는다.
//   기준 지역(baseline)이 함께 실패했으면 지역 문제가 아니라 서비스 장애다.
//   섞으면 이 화면이 또 하나의 장애 알림이 되어 정작 지역 이슈가 묻힌다.
//
// 🔴 '측정 안 함' 을 정상으로 칠하지 않는다.
//   점검 대상이 아니거나 진입점만 등재된 제품을 초록으로 보여주면 화면이 거짓말을
//   한다. 실제로 RAYGuard 는 프론트엔드가 7개 도메인에 의존했고 하나만 막혀도
//   앱이 멈췄다 — 진입점만 정상이어도 제품은 죽을 수 있다.

const pct = (ok, total) => (total ? ok / total : 1);

function cellColor(ok, total) {
  const r = pct(ok, total);
  if (r >= 1) return { bg: 'transparent', fg: 'text.primary' };
  if (r >= 0.67) return { bg: 'rgba(210,153,34,0.28)', fg: 'warning.main' };
  return { bg: 'rgba(198,69,61,0.3)', fg: 'error.main' };
}

export default function ProductReachabilityTab({ productId, state }) {
  const { data, error, loading } = state;

  const view = useMemo(() => {
    if (!data) return null;
    const all = data.measurements || [];
    const mine = all.filter((m) => m.product === productId);
    const skipped = (data.skipped || []).find((s) => s.product === productId) || null;
    const partial = (data.partial_coverage || []).find((s) => s.product === productId) || null;

    // 이 제품에서 등장하는 국가 — 기준 지역을 맨 앞에 둔다(비교 기준이므로).
    const baseline = data.baseline || null;
    const countries = [...new Set(mine.flatMap((m) => (m.countries || []).map((c) => c.country)))];
    countries.sort((a, b) => {
      if (a === baseline) return -1;
      if (b === baseline) return 1;
      return a.localeCompare(b);
    });

    // 🔴 지역 이슈로 세는 것은 기준 지역이 정상인 경우뿐이다.
    const regional = [];
    const outage = [];
    for (const m of mine) {
      if (m.error) continue;
      // 🔴 baseline_ok 가 boolean 이 아니면 기준 지역 상태를 모른다는 뜻이다.
      //    '지역 이슈' 로 넣으면 기준선이 정상이었다고 단정하는 것이고, '장애' 로
      //    넣으면 그 반대를 단정한다. 모르는 것을 단정하지 않고 어느 쪽에도 세지
      //    않는다 — 표에는 그대로 보이므로 정보를 잃지는 않는다.
      //    (피드 계약상 측정 성공 건은 항상 boolean 이고, 실패 건은 위에서 걸러진다.)
      if (typeof m.baseline_ok !== 'boolean') continue;
      for (const c of m.countries || []) {
        if (c.country === baseline || c.ok >= c.total) continue;
        (m.baseline_ok === false ? outage : regional).push({ host: m.host, ...c });
      }
    }
    return { mine, skipped, partial, baseline, countries, regional, outage };
  }, [data, productId]);

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">지역 도달성을 불러오는 중…</Typography>
      </Stack>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ fontSize: '0.88em' }}>지역 도달성을 불러오지 못했습니다 — {error}</Alert>;
  }
  if (!view) return null;

  const { mine, skipped, partial, baseline, countries, regional, outage } = view;

  // 피드가 아직 상세를 싣지 않는 버전이거나, 이 제품이 측정 대상이 아닌 경우.
  if (mine.length === 0) {
    return (
      <Stack spacing={1.5}>
        {skipped ? (
          <Alert severity="warning" sx={{ fontSize: '0.88em' }}>
            <b>이 제품은 지역 도달성 점검 대상이 아닙니다.</b> — {skipped.reason}
            {skipped.owner && ` (담당 ${skipped.owner})`}
            <br />
            점검 대상에 없다는 것은 <b>정상이라는 뜻이 아닙니다.</b> 엔드포인트를 등재해야 측정이 시작됩니다.
          </Alert>
        ) : data.detail_available === false ? (
          <Alert severity="info" sx={{ fontSize: '0.88em' }}>
            점검 결과에 국가별 상세가 아직 포함되지 않았습니다 — 다음 주간 점검부터 표시됩니다.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ fontSize: '0.88em' }}>
            이 제품의 측정 결과가 없습니다. 주간 점검이 아직 이 제품을 측정하지 않았을 수 있습니다.
          </Alert>
        )}
        <SourceLine data={data} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {/* ── 요약 ─────────────────────────────────────── */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
        <PublicIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
        {regional.length === 0 && outage.length === 0 ? (
          <Chip label="지역 차등 없음" size="small" color="success" />
        ) : (
          <>
            {regional.length > 0 && (
              <Chip label={`지역 도달성 이슈 ${regional.length}건`} size="small" color="error" />
            )}
            {outage.length > 0 && (
              <Tooltip title="기준 지역에서도 실패했습니다 — 지역 문제가 아니라 서비스 장애입니다" arrow>
                <Chip label={`서비스 장애 의심 ${outage.length}건`} size="small" color="warning" variant="outlined" />
              </Tooltip>
            )}
          </>
        )}
        <Typography variant="caption" color="text.secondary">
          엔드포인트 {mine.length}개 · 기준 지역 {baseline || '—'}
        </Typography>
      </Stack>

      {partial && (
        <Alert severity="warning" sx={{ fontSize: '0.85em' }}>
          <b>진입점만 등재된 제품입니다.</b> 제품이 의존하는 API·에셋 도메인은 아직 점검 대상이 아닙니다 —
          <b> 여기가 정상이어도 제품은 멈출 수 있습니다.</b>
        </Alert>
      )}

      {/* ── 엔드포인트 × 국가 ────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>엔드포인트</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>심각도</TableCell>
              {countries.map((c) => (
                <TableCell key={c} align="center" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: c === baseline ? 700 : 400 }}>
                  {c}{c === baseline ? ' (기준)' : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {mine.map((m) => (
              <TableRow key={`${m.host}${m.path || ''}`} hover>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                  {m.host}
                  {m.baseline_ok === false && (
                    <Tooltip title="기준 지역에서도 실패 — 지역 문제가 아니라 서비스 장애입니다" arrow>
                      <Chip label="서비스 장애" size="small" color="warning" variant="outlined" sx={{ ml: 0.75, fontSize: 10, height: 18 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 11 }}>{m.severity || '—'}</TableCell>
                {countries.map((cc) => {
                  const c = (m.countries || []).find((x) => x.country === cc);
                  if (!c) return <TableCell key={cc} align="center" sx={{ color: 'text.disabled' }}>—</TableCell>;
                  const col = cellColor(c.ok, c.total);
                  const failed = c.ok < c.total;
                  return (
                    <Tooltip
                      key={cc}
                      arrow
                      title={failed && c.layers?.length ? `막힌 지점: ${c.layers.join(', ')}` : ''}
                      disableHoverListener={!failed || !c.layers?.length}
                    >
                      <TableCell
                        align="center"
                        sx={{
                          bgcolor: col.bg, color: col.fg,
                          fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
                          fontWeight: failed ? 700 : 400, whiteSpace: 'nowrap',
                        }}
                      >
                        {c.ok}/{c.total}
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
            {mine.some((m) => m.error) && (
              <TableRow>
                <TableCell colSpan={countries.length + 2} sx={{ fontSize: 11, color: 'warning.main' }}>
                  측정 오류가 있는 엔드포인트가 있습니다 — 그 행의 국가 칸은 비어 있습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <SourceLine data={data} />
    </Stack>
  );
}

function SourceLine({ data }) {
  return (
    <Typography variant="caption" color="text.secondary">
      기준 {data.generated || '—'} ({data.week || '—'}) · 주 1회 측정 ·{' '}
      <Link href={`https://github.com/${data.sourceRepo}/blob/main/${data.sourcePath}`} target="_blank" rel="noreferrer">
        점검 결과 <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
      </Link>
      {data.reportPath && (
        <>
          {' · '}
          <Link href={`https://github.com/${data.sourceRepo}/blob/main/${data.reportPath}`} target="_blank" rel="noreferrer">
            주간 보고서 <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
          </Link>
        </>
      )}
      {data.incomplete && ' · ⚠️ 불완전 실행 (일부 측정 실패)'}
    </Typography>
  );
}
